/**
 * useFunnelTracking - Hook for tracking wizard/funnel steps
 * Measures conversion and drop-off rates
 */
import { useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/utils/api';

// Generate session ID for anonymous tracking
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('funnel_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('funnel_session_id', sessionId);
  }
  return sessionId;
};

/**
 * Track funnel events for conversion analytics
 * 
 * @param {string} funnelName - Name of the funnel (kennel_wizard, dog_wizard, etc.)
 * @returns {object} - Tracking functions
 * 
 * Usage:
 * const { trackStep, trackStart, trackComplete, trackError } = useFunnelTracking('kennel_wizard');
 * 
 * // On wizard start
 * trackStart();
 * 
 * // On each step
 * trackStep('step_1', 'Dane podstawowe', { field_count: 5 });
 * 
 * // On completion
 * trackComplete({ entity_id: 'kennel-123' });
 * 
 * // On error/abandon
 * trackError('step_3', 'validation_error', { field: 'email' });
 */
export function useFunnelTracking(funnelName) {
  const sessionId = useRef(getSessionId());
  const stepStartTime = useRef(null);
  const currentStep = useRef(null);
  
  // Get auth token if available
  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  
  // Send tracking event
  const sendEvent = useCallback(async (step, stepName = null, metadata = {}) => {
    try {
      await axios.post(`${API}/analytics/funnel/track`, {
        funnel_name: funnelName,
        step,
        step_name: stepName,
        session_id: sessionId.current,
        metadata: {
          ...metadata,
          timestamp: new Date().toISOString(),
          url: window.location.pathname,
          referrer: document.referrer || null,
          screen_width: window.innerWidth,
          screen_height: window.innerHeight
        }
      }, {
        headers: getHeaders()
      });
    } catch (error) {
      // Silently fail - analytics should not break the app
      console.debug('[Funnel] Track error:', error.message);
    }
  }, [funnelName]);
  
  // Track step with time measurement
  const trackStep = useCallback((step, stepName = null, metadata = {}) => {
    // Calculate time on previous step
    let timeOnPrevStep = null;
    if (stepStartTime.current && currentStep.current) {
      timeOnPrevStep = Date.now() - stepStartTime.current;
    }
    
    // Update current step
    currentStep.current = step;
    stepStartTime.current = Date.now();
    
    // Send event with time data
    sendEvent(step, stepName, {
      ...metadata,
      time_on_prev_step_ms: timeOnPrevStep
    });
  }, [sendEvent]);
  
  // Track funnel start
  const trackStart = useCallback((metadata = {}) => {
    // Reset session for new funnel attempt
    sessionId.current = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('funnel_session_id', sessionId.current);
    
    currentStep.current = 'start';
    stepStartTime.current = Date.now();
    
    sendEvent('start', 'Start', {
      ...metadata,
      user_agent: navigator.userAgent,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
  }, [sendEvent]);
  
  // Track funnel completion
  const trackComplete = useCallback((metadata = {}) => {
    const totalTime = stepStartTime.current ? Date.now() - stepStartTime.current : null;
    
    sendEvent('complete', 'Zakończono', {
      ...metadata,
      total_time_ms: totalTime
    });
    
    // Reset for potential new funnel
    currentStep.current = null;
    stepStartTime.current = null;
  }, [sendEvent]);
  
  // Track error or validation failure
  const trackError = useCallback((step, errorType, metadata = {}) => {
    sendEvent(`error_${step}`, `Błąd: ${errorType}`, {
      ...metadata,
      error_type: errorType,
      current_step: currentStep.current
    });
  }, [sendEvent]);
  
  // Track abandonment (on unmount without completion)
  const trackAbandon = useCallback(() => {
    if (currentStep.current && currentStep.current !== 'complete') {
      sendEvent('abandon', 'Porzucono', {
        last_step: currentStep.current,
        time_spent_ms: stepStartTime.current ? Date.now() - stepStartTime.current : null
      });
    }
  }, [sendEvent]);
  
  // Track on unmount (abandonment detection)
  useEffect(() => {
    // Track abandon on page unload
    const handleBeforeUnload = () => {
      if (currentStep.current && currentStep.current !== 'complete') {
        // Use sendBeacon for reliable tracking on page close
        const data = JSON.stringify({
          funnel_name: funnelName,
          step: 'abandon',
          step_name: 'Porzucono (zamknięcie strony)',
          session_id: sessionId.current,
          metadata: {
            last_step: currentStep.current,
            timestamp: new Date().toISOString()
          }
        });
        
        navigator.sendBeacon(`${API}/analytics/funnel/track`, data);
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      trackAbandon();
    };
  }, [funnelName, trackAbandon]);
  
  return {
    trackStep,
    trackStart,
    trackComplete,
    trackError,
    trackAbandon,
    sessionId: sessionId.current
  };
}

/**
 * Standalone tracking function for one-off events
 * Use when hook is not practical
 */
export async function trackFunnelEvent(funnelName, step, stepName = null, metadata = {}) {
  try {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    await axios.post(`${API}/analytics/funnel/track`, {
      funnel_name: funnelName,
      step,
      step_name: stepName,
      session_id: sessionStorage.getItem('funnel_session_id'),
      metadata
    }, { headers });
  } catch (error) {
    console.debug('[Funnel] Track error:', error.message);
  }
}

export default useFunnelTracking;
